import { useMemo, useState } from "react";
import type { PlantSummary } from "../../shared/types";
import {
  collectGrowingFilterKeys,
  collectLocationFilterKeys,
  growingMediumFilterLabel,
  locationFilterLabel,
} from "../plantMetadata";
import { MetadataFilterDropdown } from "./MetadataFilterDropdown";

type Props = {
  plants: PlantSummary[];
  locationFilter: Set<string>;
  onLocationFilterChange: (next: Set<string>) => void;
  growingFilter: Set<string>;
  onGrowingFilterChange: (next: Set<string>) => void;
  filteredCount: number;
};

export function PlantMetadataFilters(props: Props) {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const locationKeys = useMemo(() => collectLocationFilterKeys(props.plants), [props.plants]);
  const growingKeys = useMemo(() => collectGrowingFilterKeys(props.plants), [props.plants]);

  const locationOptions = useMemo(
    () => locationKeys.map((key) => ({ key, label: locationFilterLabel(key) })),
    [locationKeys],
  );
  const growingOptions = useMemo(
    () => growingKeys.map((key) => ({ key, label: growingMediumFilterLabel(key) })),
    [growingKeys],
  );

  const active = props.locationFilter.size > 0 || props.growingFilter.size > 0;

  if (props.plants.length === 0) return null;

  return (
    <section className="metadata-filters panel" aria-label="Filter plants by metadata">
      <div className="metadata-filters__row">
        <div className="metadata-filters__dropdowns">
          <MetadataFilterDropdown
            id="location"
            label="Location"
            allLabel="All locations"
            options={locationOptions}
            selected={props.locationFilter}
            onChange={props.onLocationFilterChange}
            openId={openDropdown}
            onOpenIdChange={setOpenDropdown}
          />
          <MetadataFilterDropdown
            id="growing"
            label="Growing"
            allLabel="All growing types"
            options={growingOptions}
            selected={props.growingFilter}
            onChange={props.onGrowingFilterChange}
            openId={openDropdown}
            onOpenIdChange={setOpenDropdown}
          />
        </div>
        <p className="metadata-filters__count muted">
          {props.filteredCount} of {props.plants.length}
        </p>
        {active ? (
          <button
            type="button"
            className="btn btn-ghost metadata-filters__clear"
            onClick={() => {
              props.onLocationFilterChange(new Set());
              props.onGrowingFilterChange(new Set());
            }}
          >
            Clear
          </button>
        ) : null}
      </div>
    </section>
  );
}
